// Silence CRA's react-error-overlay before ANY other module loads.
// Auth-flow transient 401s during refresh-token rotation are an expected
// state in this app; surfacing them as a full-page "Uncaught runtime
// errors" overlay is dev-mode noise that obscures the real UI. Real JS
// bugs still log to the browser console + DevTools so debugging is
// unaffected. In production builds, react-error-overlay isn't bundled,
// so this no-ops.
if (process.env.NODE_ENV !== "production") {
  try {
    const reo = require("react-error-overlay");
    if (reo && typeof reo.stopReportingRuntimeErrors === "function") {
      reo.stopReportingRuntimeErrors();
    }
  } catch (_) { /* package not present */ }

  // iter-156cu — Swallow the benign "ResizeObserver loop completed
  // with undelivered notifications" warning that webpack-dev-server's
  // overlay treats as an uncaught error. The warning is fired by
  // Radix UI's <Select> / <DialogContent> when the dropdown opens
  // inside a dialog (both measure layout in overlapping animation
  // frames). It does NOT indicate a real bug — the UI works
  // correctly — but the overlay stack covers the entire page and
  // forces a dismiss click. Browsers spec this as a warning, not an
  // error; we restore that semantic. react-error-overlay shim above
  // doesn't catch this because webpack-dev-server's overlay client
  // has its own `window.error` listener.
  //
  // Capture-phase listener runs BEFORE bubble-phase listeners (which
  // is what webpack-dev-server installs).
  const _swallowResizeObserverLoop = (e) => {
    const msg = (e && (e.message || (e.reason && e.reason.message))) || "";
    if (typeof msg === "string" && msg.toLowerCase().includes("resizeobserver loop")) {
      e.stopImmediatePropagation();
      if (typeof e.preventDefault === "function") e.preventDefault();
      return false;
    }
    return undefined;
  };
  window.addEventListener("error", _swallowResizeObserverLoop, true);
  window.addEventListener("unhandledrejection", _swallowResizeObserverLoop, true);
}

import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { installApiCacheInterceptor } from "@/lib/apiCacheInterceptor";
import { installClientErrorReporter, ClientErrorBoundary } from "@/lib/clientErrorReporter";
import { installTranslateGuard } from "@/lib/translateGuard";

// iter-156cb — Auto-invalidate /api/jobs, /api/team, /api/crews
// cache entries on every successful mutation (POST/PUT/PATCH/DELETE).
// Removes the need to call invalidate(…) at every callsite.
installApiCacheInterceptor();

// iter-156dc — Frontend error reporter. Captures React render
// crashes via the ErrorBoundary below + `window.error` and
// `window.unhandledrejection` via window listeners. All routes
// land in `db.system_logs` with `category: "client_error"`,
// visible to platform admins in the System Logs tab.
installClientErrorReporter();

// Phones that auto-translate the page (crew on Spanish Chrome) must not crash React — see lib/translateGuard.
installTranslateGuard();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ClientErrorBoundary>
      <App />
    </ClientErrorBoundary>
  </React.StrictMode>,
);
